import { useEffect, useCallback, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { KNM_HISTORY, KNM_EXECUTION_HISTORY } from 'CorePackage/MainModule/routes/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmHistoryChanges } from 'KnmPackage/MainModule/interfaces/IKnmHistoryChanges';
import {
  decisionSetHistoryChangesIsOn,
  decisionGetHistoryChanges,
} from 'DecisionPackage/MainModule/actions/decisionActions';

import { KnmHistory } from '../../../KnmPackage/KnmHistoriesModule/components/KnmHistory';
import {
  getDecisionHistoryChanges,
  getDecisionHistoryChangesIsLoading,
  getDecisionHistoryChangesIsOn,
} from '../selectors/decisionSelectors';

interface IDecisionHistoriesProps {
  title: string;
  executionTitle?: string;
  isOnDecisionHistoryChanges: boolean;
  decisionHistoryChanges?: IKnmHistoryChanges;
  isLoadingDecisionHistoryChanges: boolean;
}

export const DecisionHistoriesWithoutStore = ({
  title,
  executionTitle,
  isOnDecisionHistoryChanges,
  decisionHistoryChanges,
  isLoadingDecisionHistoryChanges,
}: IDecisionHistoriesProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const blockRef = useRef<HTMLDivElement>(null);

  const updateDidVisible = useCallback(() => {
    const blockOffsetTop = blockRef.current?.getBoundingClientRect().top;

    if (blockOffsetTop && blockOffsetTop - document.documentElement.clientHeight < 0) {
      if (!isOnDecisionHistoryChanges) {
        dispatch(decisionSetHistoryChangesIsOn(true));
        dispatch(decisionGetHistoryChanges());
      }
    }
  }, [dispatch, isOnDecisionHistoryChanges]);

  useEffect(() => {
    updateDidVisible();
    window.addEventListener('scroll', updateDidVisible);

    return () => window.removeEventListener('scroll', updateDidVisible);
  }, [updateDidVisible]);

  return (
    <>
      <KnmHistory
        id={KNM_HISTORY}
        title={title}
        blockRef={blockRef}
        loading={isLoadingDecisionHistoryChanges}
        historyChanges={decisionHistoryChanges?.main || []}
      />
      {executionTitle && (
        <KnmHistory
          id={KNM_EXECUTION_HISTORY}
          title={executionTitle}
          loading={isLoadingDecisionHistoryChanges}
          historyChanges={decisionHistoryChanges?.execution || []}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  decisionHistoryChanges: getDecisionHistoryChanges(state),
  isLoadingDecisionHistoryChanges: getDecisionHistoryChangesIsLoading(state),
  isOnDecisionHistoryChanges: getDecisionHistoryChangesIsOn(state),
});

export const DecisionHistories = connect(mapStateToProps)(DecisionHistoriesWithoutStore);
